import { getAuthorizationToken } from '../../../../common/sgwt-widgets-utils';
import type {
  AlternateTokenGetterFunction,
  DxAgentFactoryContext,
  SgwtChatbotAdapter,
  SgwtChatbotContext,
  SgwtChatbotWidgetPublishMessageHandler,
} from '../../../../common/types/sgwt-chatbot.adapter';
import type { SgwtChatbotConversation, SgwtChatbotMessage, SgwtChatbotUserHistory } from '../../shared/chatbot.types';
import type { DxAgentFactoryPrompt, DxAgentFactoryPromptResponse } from './dx-agent-factory.types';
import { DxConverter } from './dx-agent-factory.utils';

const API_URL = {
  development: 'https://dx-dev.fr.world.socgen/api/assistant/api/v1/agents',
  homologation: 'https://developer-uat.fr.world.socgen/api/assistant/api/v1/agents',
  production: 'https://developer.fr.world.socgen/api/assistant/api/v1/agents',
};

// cf. `help-center.endpoints.ts` file
const FEEDBACK_URL = {
  development: 'https://sgwt-api-sgwt-help-center-dev.sgmarkets.world.socgen/api/v1/feedback',
  homologation: 'https://sgwt-api-sgwt-help-center-uat.sgmarkets.world.socgen/api/v1/feedback',
  production: 'https://sgwt-api-sgwt-help-center.sgmarkets.world.socgen/api/v1/feedback',
};

export class DxAgentFactoryAdapter implements SgwtChatbotAdapter {
  private readonly apiUrl: string;

  // This handler is used to send messages asynchronously to the chat widget
  private messageHandler: SgwtChatbotWidgetPublishMessageHandler | null = null;

  // External function to retrieve an alternate token.
  private alternateTokenGetter: AlternateTokenGetterFunction | null = null;

  constructor(rawContext: SgwtChatbotContext) {
    const context = rawContext as DxAgentFactoryContext;
    const env = context.apiEnvironment?.toLowerCase() ?? '';
    if (['prod', 'prd', 'production'].includes(env)) {
      this.apiUrl = API_URL.production;
    } else if (['homologation', 'uat'].includes(env)) {
      this.apiUrl = API_URL.homologation;
    } else if (['dev', 'development'].includes(env)) {
      this.apiUrl = API_URL.development;
    } else {
      this.apiUrl = API_URL[context.environment];
    }
    this.apiUrl += `/${context.agentId}`;
  }

  public setPublishMessageHandler(handler: SgwtChatbotWidgetPublishMessageHandler) {
    this.messageHandler = handler;
  }

  public setAlternateTokenGetterFunction = (handler: AlternateTokenGetterFunction) => {
    if (typeof handler === 'function') {
      console.debug('<sgwt-chatbot> Alternate token getter function has been set.');
      this.alternateTokenGetter = handler;
    } else {
      console.error('<sgwt-chatbot> The provided handler is not a function, please provide a valid function.');
    }
  };

  get name(): string {
    return 'dx-agent-factory';
  }

  private async callApi<T>(url: string, method: 'GET' | 'POST' = 'GET', body?: string): Promise<T> {
    const alternateToken: string = this.alternateTokenGetter ? await this.alternateTokenGetter() : '';

    return fetch(url, {
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        authorization: getAuthorizationToken() || '',
        'x-Alternate-Token': alternateToken ? `Bearer ${alternateToken.replace('Bearer ', '')}` : '',
      },
      method,
      mode: 'cors',
      body,
    })
      .then((response: Response) => {
        if (response.ok) {
          return response;
        }
        throw new Error(response.statusText);
      })
      .then((response) => response.json());
  }

  public async onUserMessage(_context: SgwtChatbotContext, userMessage: SgwtChatbotMessage): Promise<SgwtChatbotMessage | null> {
    const dxPrompt: DxAgentFactoryPrompt = {
      prompt: userMessage.message.content,
      conversationId: userMessage.conversationId,
    };

    const dxResponse = await this.callApi<DxAgentFactoryPromptResponse>(`${this.apiUrl}/_prompt`, 'POST', JSON.stringify(dxPrompt));

    if (!dxResponse) {
      return null;
    }

    return DxConverter.convertPromptResponse(dxResponse);
  }

  public async getHistory(_context?: SgwtChatbotContext): Promise<SgwtChatbotUserHistory> {
    // TODO Not implemented on API side yet.
    return { conversations: [] };
  }

  public async getConversation(_context: SgwtChatbotContext, _conversationId: string): Promise<SgwtChatbotConversation | null> {
    // TODO Not implemented on API side yet.
    return null;
  }

  // Post the user reaction as a feedback message to the Help Center API
  public async onUserFeedback(
    rawContext: SgwtChatbotContext,
    message: SgwtChatbotMessage,
    reaction: 'like' | 'dislike',
    explanations: string,
    _conversation: SgwtChatbotMessage[],
  ): Promise<void> {
    const context = rawContext as DxAgentFactoryContext;
    const url = window.location.toString();
    const domain = url.substring(0, url.indexOf('/', 8));
    const { user, environment, feedbackApplicationId } = context;
    const applicationId = feedbackApplicationId ?? 'developer';

    const content = `Feedback sent from ${domain}
Full URL: ${url}
Application ID: ${applicationId}
Context:
Agent ID: ${context.agentId}
Message ID: ${message.id}
Conversation ID: ${message.conversationId}

###

${explanations}
`;

    const body = {
      content: btoa(encodeURIComponent(content)),
      date: new Date().toISOString(),
      email: user?.mail ?? '',
      from: user?.mail ?? '',
      username: user?.name ?? '',
      rating: reaction === 'like' ? 5 : 1,
      subject: `[DX-Assistant User feedback] ${reaction}`,
      url,
      userAgent: window.navigator.userAgent,
      topicId: 'ai-message-reaction', // Useful?
    };

    const feedbackApi = `${FEEDBACK_URL[environment]}?application-id=${applicationId}`;

    return this.callApi(feedbackApi, 'POST', JSON.stringify(body));
  }
}
